import { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Mail, MessageCircle, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import type { CommentResponse } from "../../gen/models";
import { HighlightedMessageText } from "./HighlightedMessageText";

export interface ResidenceOption {
  slug?: string;
  title?: string;
  residence_name?: string;
  rate_name?: string;
  room_size?: number;
  bedrooms_summary?: string;
  highlighted_amenities?: string[];
  nightly_rate_usd?: number;
  average_nightly_rate_usd?: number;
  total_price_usd?: number;
  direct_booking_url?: string;
}

export interface StayProposalCardProps {
  quoteDraft: {
    stay_dates_confirmed?: string;
    email_subject?: string;
    email_body_text?: string;
    instagram_dm_text?: string;
    alternative_explanation?: string;
    is_alternative_dates?: boolean;
    message?: string;
    quote_options?: ResidenceOption[];
    options?: ResidenceOption[];
    first_name?: string;
    last_name?: string;
  };
  msgId?: string;
  comments?: CommentResponse[];
  activeHighlightedCommentId?: string | null;
  activeSelection?: {
    text: string;
    start?: number;
    end?: number;
  } | null;
  onSelectText?: (messageId: string) => void;
  onCommentClick?: (commentId: string) => void;
}

export function StayProposalCard({
  quoteDraft,
  msgId,
  comments = [],
  activeHighlightedCommentId = null,
  activeSelection,
  onSelectText,
  onCommentClick,
}: StayProposalCardProps) {
  const [isInstagramExpanded, setIsInstagramExpanded] = useState(false);

  const markdownContent =
    quoteDraft.email_body_text ||
    quoteDraft.message ||
    quoteDraft.instagram_dm_text ||
    "";

  const options = quoteDraft.quote_options || quoteDraft.options || [];

  return (
    <div className="space-y-4 rounded-2xl border border-stone-300 dark:border-stone-800/80 bg-stone-100/70 dark:bg-stone-950/40 p-4 md:p-5">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 dark:border-stone-800/80 pb-3">
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold text-sm">
          <Sparkles className="w-4 h-4 shrink-0" />
          <span>Luxury Stay Proposal</span>
        </div>
        <span className="text-xs text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/60 px-2.5 py-0.5 rounded-full font-medium">
          {quoteDraft.stay_dates_confirmed || "Custom Dates"}
        </span>
      </div>

      {/* Email Subject Line if available */}
      {quoteDraft.email_subject && (
        <div className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300 bg-white/70 dark:bg-stone-900/60 px-3 py-2 rounded-xl border border-stone-200 dark:border-stone-800">
          <Mail className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="font-semibold text-stone-500 dark:text-stone-400">Subject:</span>
          <span className="font-medium text-stone-900 dark:text-stone-100">{quoteDraft.email_subject}</span>
        </div>
      )}

      {/* Alternative dates banner */}
      {quoteDraft.alternative_explanation && (
        <div className="text-xs text-amber-800 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30 p-3 rounded-xl border border-amber-200/70 dark:border-amber-800/50 leading-relaxed">
          <span className="font-semibold">Alternative Dates Recommended: </span>
          {quoteDraft.alternative_explanation}
        </div>
      )}

      {/* Rendered Markdown Body */}
      {markdownContent ? (
        <div className="text-sm leading-relaxed text-stone-800 dark:text-stone-200">
          {msgId && onCommentClick ? (
            <HighlightedMessageText
              msgId={msgId}
              content={markdownContent}
              comments={comments}
              activeHighlightedCommentId={activeHighlightedCommentId}
              activeSelection={activeSelection}
              onSelectText={onSelectText}
              onCommentClick={onCommentClick}
            />
          ) : (
            <div className="prose prose-stone dark:prose-invert max-w-none text-sm leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {markdownContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      ) : null}

      {/* Residence Options Cards */}
      {options && options.length > 0 && (
        <div className="space-y-2.5 pt-1">
          <div className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wider">
            Verified Residence Options
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {options.map((opt, idx) => {
              const optTitle = opt.title || opt.residence_name || "Residence";
              const nightlyRate = opt.nightly_rate_usd ?? opt.average_nightly_rate_usd;
              const totalPrice = opt.total_price_usd;
              const subDesc = opt.bedrooms_summary || opt.rate_name || "Direct Web Rate";
              const amenities = opt.highlighted_amenities || [];

              return (
                <div
                  key={idx}
                  className="p-3.5 rounded-xl bg-white dark:bg-stone-900/70 flex flex-col justify-between gap-3 border border-stone-200 dark:border-stone-800/80 shadow-2xs"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-stone-900 dark:text-stone-100 text-sm">
                        {optTitle}
                      </div>
                      {opt.room_size ? (
                        <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono shrink-0">
                          {opt.room_size} sq ft
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                      {subDesc}
                    </div>

                    {amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {amenities.map((amenity, aIdx) => (
                          <span
                            key={aIdx}
                            className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-200/60 dark:border-stone-700/60"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-end justify-between border-t border-stone-100 dark:border-stone-800 pt-2.5 mt-1">
                    <div>
                      {totalPrice !== undefined && (
                        <div className="text-base font-bold text-emerald-700 dark:text-emerald-400">
                          ${Number(totalPrice).toLocaleString()}
                        </div>
                      )}
                      {nightlyRate !== undefined && (
                        <div className="text-[11px] text-stone-500 dark:text-stone-400 font-medium">
                          ${Number(nightlyRate).toLocaleString()}/night
                        </div>
                      )}
                    </div>

                    {opt.direct_booking_url && (
                      <a
                        href={opt.direct_booking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow-2xs transition"
                      >
                        <span>Book</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Instagram DM version toggle if different */}
      {quoteDraft.instagram_dm_text && quoteDraft.instagram_dm_text !== markdownContent && (
        <div className="pt-2 border-t border-stone-200 dark:border-stone-800">
          <button
            type="button"
            onClick={() => setIsInstagramExpanded(!isInstagramExpanded)}
            className="cursor-pointer text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 font-medium inline-flex items-center gap-1.5 select-none transition"
          >
            <MessageCircle className="w-3.5 h-3.5 text-pink-500 shrink-0" />
            <span>Instagram DM Copy</span>
            {isInstagramExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-stone-400" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
            )}
          </button>
          {isInstagramExpanded && (
            <div className="mt-2.5 p-3 rounded-xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 whitespace-pre-wrap text-stone-700 dark:text-stone-300 font-mono text-xs leading-relaxed">
              {quoteDraft.instagram_dm_text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
